"use client";

/**
 * ExplorerView - Explorer UI for agent selection and conversation start
 *
 * Supports two resource binding modes:
 * - fixed: Agent has pre-configured MCP servers, user selects resources from dropdowns
 * - dynamic: User can add/remove resources dynamically (Explorer agent)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  Search,
  ChevronRight,
  Check,
  Server,
  Loader2,
  SendHorizonal,
  ChevronDown,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { getMcpBaseUrl } from "@/lib/apiClient";
import { AgentSearchDropdown, type CatalogItem } from "./agent-search-dropdown";

// =============================================================================
// Types
// =============================================================================

export interface Agent {
  agent_id: string;
  execution_id?: string;
  name: string;
  display_name?: string;
  short_description?: string;
  long_description?: string;
  description?: string;
  icon?: string;
  category?: string;
  version?: string;
  capabilities?: string[];
  usage_examples?: string[];
  tags?: string[];
  enabled?: boolean;
  resource_binding_mode?: 'fixed' | 'dynamic';
  mcp_servers?: MCPServerConfig[];
  /** Base path for file-based agents (catalog root directory) */
  basePath?: string;
}

/** New MCP server config format: name + type */
interface MCPServerConfig {
  name: string;           // Semantic name (e.g., "source_db", "target_db")
  type: string;           // Resource type (e.g., "postgres", "platform_mcp.tcm")
  timeout?: number;
}

interface McpServer {
  name: string;
  display_name: string;
  type?: string;
  description?: string;
  category?: string;
  runtime?: string;
  supported_types?: string[];
  resources?: McpResource[];
}

interface McpResource {
  uri?: string;
  id?: string;
  res_id?: string;
  name: string;
  description?: string;
  is_default?: boolean;
}

interface Model {
  id: string;
  name: string;
}

/** Resource bindings: semantic name -> res_id */
interface ResourceBindings {
  [name: string]: string;
}

export interface ExplorerViewProps {
  /** API base URL for fetching models */
  apiBaseUrl: string;
  /** MCP base URL for fetching servers */
  mcpBaseUrl?: string;
  /** Currently selected agent */
  currentAgent: Agent;
  /** List of available agents for left panel */
  availableAgents?: Agent[];
  /** Whether to show agent list panel - only true for router/explorer agents */
  showAgentList?: boolean;
  /** Whether to show MCP resources section */
  showMcpResources?: boolean;
  /** Tenant ID for multi-tenancy */
  tenantId?: string;
  /** User ID for tracking */
  userId?: string;
  /** Callback when user starts conversation */
  onStart: (params: {
    agent: Agent;
    model: string;
    resource_bindings: ResourceBindings;
    goal: string;
  }) => void;
  /** Callback when user browses/previews a different agent */
  onAgentChange?: (agent: Agent) => void;
  /** Custom class name */
  className?: string;
  /** Enable @ commands for skill/agent search */
  enableAtCommands?: boolean;
  /** Catalog items for @ search (agents and skills) */
  searchCatalog?: CatalogItem[];
}

// =============================================================================
// Constants
// =============================================================================

const PLATFORM_RESOURCE_TYPES = new Set([
  "platform_mcp.tcm",
  "platform_mcp.metadata",
  "platform_mcp.observability",
  "platform_mcp.filesystem",
  "platform_mcp.eureka_flows",
  "platform_mcp.governance",
  "platform_mcp.test-data",
]);

/** Check if MCP server type requires user binding */
function requiresUserBinding(type: string): boolean {
  return !PLATFORM_RESOURCE_TYPES.has(type);
}

/** Get resource binding mode for agent */
function getResourceBindingMode(agent: Agent): 'fixed' | 'dynamic' {
  if (agent.resource_binding_mode) {
    return agent.resource_binding_mode;
  }
  if (agent.tags?.includes('router') || agent.tags?.includes('explorer')) {
    return 'dynamic';
  }
  return 'fixed';
}

// =============================================================================
// Component
// =============================================================================

export function ExplorerView({
  apiBaseUrl,
  mcpBaseUrl,
  currentAgent,
  availableAgents = [],
  showAgentList = false,
  showMcpResources = true,
  tenantId,
  userId,
  onStart,
  onAgentChange,
  className,
  enableAtCommands = false,
  searchCatalog = [],
}: ExplorerViewProps) {
  // Agent state
  const [selectedAgent, setSelectedAgent] = useState<Agent>(currentAgent);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // @ Command state
  const [showAtSearch, setShowAtSearch] = useState(false);
  const [atSearchQuery, setAtSearchQuery] = useState("");

  // Model state
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [modelsLoading, setModelsLoading] = useState(true);

  // MCP state - all available servers from API (with resources)
  const [availableMcpServers, setAvailableMcpServers] = useState<McpServer[]>([]);
  const [mcpLoading, setMcpLoading] = useState(false);

  // Agent config state
  const [agentMcpConfig, setAgentMcpConfig] = useState<MCPServerConfig[]>([]);

  // Resource bindings for fixed mode: semantic name -> res_id
  const [resourceBindings, setResourceBindings] = useState<ResourceBindings>({});

  // Dynamic mode state (for Explorer)
  const [dynamicResources, setDynamicResources] = useState<Array<{ name: string; type: string; res_id: string }>>([]);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [newResourceName, setNewResourceName] = useState("");
  const [newResourceType, setNewResourceType] = useState("");
  const [newResourceId, setNewResourceId] = useState("");

  // Input state
  const [goalInput, setGoalInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Computed values
  const bindingMode = getResourceBindingMode(selectedAgent);
  const tenantMcpServers = agentMcpConfig.filter(s => requiresUserBinding(s.type));
  const platformMcpServers = agentMcpConfig.filter(s => !requiresUserBinding(s.type));

  // Update selected agent when prop changes
  useEffect(() => {
    setSelectedAgent(currentAgent);
  }, [currentAgent]);

  // =============================================================================
  // Fetch Models
  // =============================================================================

  useEffect(() => {
    async function fetchModels() {
      try {
        const res = await fetch(`${apiBaseUrl}/lifecycle/models`, {
          headers: { "x-requested-with": "XMLHttpRequest" },
        });
        if (res.ok) {
          const data = await res.json();
          const modelList = data.models || [];
          setModels(modelList);
          if (modelList.length > 0) setSelectedModel(modelList[0].id);
        }
      } catch {
        setModels([
          { id: "claude-sonnet-4", name: "Claude Sonnet 4" },
          { id: "claude-haiku-4", name: "Claude Haiku 4" },
        ]);
        setSelectedModel("claude-sonnet-4");
      } finally {
        setModelsLoading(false);
      }
    }
    fetchModels();
  }, [apiBaseUrl]);

  // =============================================================================
  // Fetch MCP Servers (all available with resources)
  // =============================================================================

  useEffect(() => {
    if (!showMcpResources) return;

    async function fetchMcpServers() {
      setMcpLoading(true);
      try {
        const url = mcpBaseUrl || getMcpBaseUrl();
        console.log('[ExplorerView] fetchMcpServers - url:', url, 'tenantId:', tenantId, 'userId:', userId);
        if (!url) {
          console.log('[ExplorerView] No MCP URL, skipping fetch');
          setMcpLoading(false);
          return;
        }
        const headers: Record<string, string> = {
          "x-requested-with": "XMLHttpRequest",
        };
        // Send multiple header variants for compatibility with different backends
        if (tenantId) {
          headers["x-tenant-id"] = tenantId;
          headers["tenant"] = tenantId;
        }
        if (userId) {
          headers["x-user-id"] = userId;
          headers["user-id"] = userId;
        }
        console.log('[ExplorerView] Fetching MCPs with headers:', headers);

        const res = await fetch(`${url}/mcps`, { headers });
        console.log('[ExplorerView] MCP response status:', res.status);
        if (res.ok) {
          const data = await res.json();
          console.log('[ExplorerView] MCP response data:', data);
          const servers: McpServer[] = Array.isArray(data) ? data : data.servers || [];
          console.log('[ExplorerView] Parsed MCP servers:', servers);
          setAvailableMcpServers(servers);
        }
      } catch (err) {
        console.warn("[ExplorerView] Error fetching MCP servers:", err);
      } finally {
        setMcpLoading(false);
      }
    }
    fetchMcpServers();
  }, [showMcpResources, mcpBaseUrl, tenantId, userId]);

  // =============================================================================
  // Fetch Agent Config (to get MCP servers with name+type)
  // =============================================================================

  useEffect(() => {
    async function fetchAgentConfig() {
      if (!selectedAgent.agent_id) return;

      console.log('[ExplorerView] fetchAgentConfig for:', selectedAgent.agent_id, 'existing mcp_servers:', selectedAgent.mcp_servers);

      // If agent already has mcp_servers in new format, use that
      if (selectedAgent.mcp_servers && selectedAgent.mcp_servers.length > 0) {
        console.log('[ExplorerView] Using existing mcp_servers:', selectedAgent.mcp_servers);
        setAgentMcpConfig(selectedAgent.mcp_servers);
        setResourceBindings({});
        setDynamicResources([]);
        return;
      }

      try {
        const headers: Record<string, string> = {
          "x-requested-with": "XMLHttpRequest",
        };
        // Send multiple header variants for compatibility with different backends
        if (tenantId) {
          headers["x-tenant-id"] = tenantId;
          headers["tenant"] = tenantId;
        }
        if (userId) {
          headers["x-user-id"] = userId;
          headers["user-id"] = userId;
        }

        const configUrl = `${apiBaseUrl}/lifecycle/agents/${encodeURIComponent(selectedAgent.agent_id)}/config`;
        console.log('[ExplorerView] Fetching agent config from:', configUrl);
        const res = await fetch(configUrl, { headers });
        console.log('[ExplorerView] Agent config response status:', res.status);
        if (res.ok) {
          const config = await res.json();
          console.log('[ExplorerView] Agent config:', config);
          const mcpServers: MCPServerConfig[] = config.mcp_servers || [];
          console.log('[ExplorerView] Setting agentMcpConfig:', mcpServers);
          setAgentMcpConfig(mcpServers);

          // Update agent with config data
          setSelectedAgent(prev => ({
            ...prev,
            resource_binding_mode: config.resource_binding_mode || prev.resource_binding_mode,
            mcp_servers: mcpServers,
            capabilities: config.capabilities || prev.capabilities,
            description: config.description || prev.description,
            usage_examples: config.usage_examples || prev.usage_examples,
            short_description: config.short_description || prev.short_description,
            long_description: config.long_description || prev.long_description,
            icon: config.icon || prev.icon,
            version: config.version || prev.version,
          }));
        } else {
          setAgentMcpConfig([]);
        }
        // Reset bindings when agent changes
        setResourceBindings({});
        setDynamicResources([]);
      } catch {
        console.warn("[ExplorerView] Failed to fetch agent config");
        setAgentMcpConfig([]);
      }
    }

    fetchAgentConfig();
  }, [selectedAgent.agent_id, apiBaseUrl, tenantId, userId]);

  // =============================================================================
  // Filter agents
  // =============================================================================

  const categories = ["all", ...new Set(availableAgents.map((a) => a.category || "uncategorized"))];

  const filteredAgents = availableAgents.filter((agent) => {
    const matchesSearch =
      !searchQuery ||
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (agent.short_description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || agent.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // =============================================================================
  // Resource Helpers
  // =============================================================================

  const getResourcesForType = useCallback((type: string): McpResource[] => {
    // Match by type field, name field, case-insensitive, or supported_types
    const typeLower = type?.toLowerCase();
    const server = availableMcpServers.find(s => {
      if (s.type === type || s.name === type) return true;
      if (s.name?.toLowerCase() === typeLower || s.type?.toLowerCase() === typeLower) return true;
      // Check if type is in supported_types array
      if (s.supported_types?.some((st: string) => st === type || st.toLowerCase() === typeLower)) return true;
      return false;
    });
    return server?.resources || [];
  }, [availableMcpServers]);

  // =============================================================================
  // Handlers
  // =============================================================================

  const handleAgentSelect = useCallback((agent: Agent) => {
    setSelectedAgent(agent);
    setResourceBindings({});
    setDynamicResources([]);
    onAgentChange?.(agent);
  }, [onAgentChange]);

  const handleResourceSelect = useCallback((semanticName: string, resId: string) => {
    setResourceBindings(prev => ({
      ...prev,
      [semanticName]: resId
    }));
  }, []);

  const handleAddDynamicResource = useCallback(() => {
    if (!newResourceName.trim() || !newResourceType || !newResourceId) return;

    setDynamicResources(prev => [
      ...prev,
      { name: newResourceName.trim(), type: newResourceType, res_id: newResourceId }
    ]);

    setShowAddResourceModal(false);
    setNewResourceName("");
    setNewResourceType("");
    setNewResourceId("");
  }, [newResourceName, newResourceType, newResourceId]);

  const handleRemoveDynamicResource = useCallback((index: number) => {
    setDynamicResources(prev => prev.filter((_, i) => i !== index));
  }, []);

  const canSubmit = useCallback(() => {
    if (!goalInput.trim()) return false;

    // For fixed mode, check all tenant resources are bound
    if (bindingMode === 'fixed') {
      for (const server of tenantMcpServers) {
        if (!resourceBindings[server.name]) return false;
      }
    }

    return true;
  }, [goalInput, bindingMode, tenantMcpServers, resourceBindings]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit() || isSubmitting) return;

    setIsSubmitting(true);

    // Build final resource bindings
    let finalBindings: ResourceBindings = {};

    if (bindingMode === 'fixed') {
      finalBindings = { ...resourceBindings };
    } else {
      // Dynamic mode - convert array to bindings
      dynamicResources.forEach(r => {
        finalBindings[r.name] = r.res_id;
      });
    }

    onStart({
      agent: selectedAgent,
      model: selectedModel,
      resource_bindings: finalBindings,
      goal: goalInput.trim(),
    });
  }, [canSubmit, isSubmitting, bindingMode, resourceBindings, dynamicResources, selectedAgent, selectedModel, goalInput, onStart]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Don't process if @ search is open - let dropdown handle it
    if (showAtSearch) return;

    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit, showAtSearch]);

  // Handle @ command input
  const handleGoalInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setGoalInput(value);

    if (enableAtCommands) {
      // Check if user just typed @ at the start or after a space
      const lastAtIndex = value.lastIndexOf('@');
      if (lastAtIndex !== -1) {
        const charBefore = lastAtIndex > 0 ? value[lastAtIndex - 1] : ' ';
        if (charBefore === ' ' || lastAtIndex === 0) {
          // Extract query after @
          const afterAt = value.slice(lastAtIndex);
          const spaceAfterQuery = afterAt.indexOf(' ', 1);
          const query = spaceAfterQuery === -1 ? afterAt : afterAt.slice(0, spaceAfterQuery);

          if (query.length >= 1) {
            setShowAtSearch(true);
            setAtSearchQuery(query);
            return;
          }
        }
      }
      setShowAtSearch(false);
      setAtSearchQuery("");
    }
  }, [enableAtCommands]);

  // Handle @ search selection
  const handleAtSearchSelect = useCallback((result: { name: string; display_name: string }) => {
    // Replace the @query with @selectedName
    const lastAtIndex = goalInput.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const before = goalInput.slice(0, lastAtIndex);
      const afterAt = goalInput.slice(lastAtIndex);
      const spaceAfterQuery = afterAt.indexOf(' ', 1);
      const after = spaceAfterQuery === -1 ? '' : afterAt.slice(spaceAfterQuery);

      setGoalInput(`${before}@${result.name}${after} `);
    }
    setShowAtSearch(false);
    setAtSearchQuery("");
    inputRef.current?.focus();
  }, [goalInput]);

  const handleAtSearchClose = useCallback(() => {
    setShowAtSearch(false);
    setAtSearchQuery("");
  }, []);

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className={cn("flex flex-row h-full w-full", className)}>
      {/* Left Panel - Agent List */}
      {showAgentList && availableAgents.length > 0 && (
        <div className="w-72 shrink-0 border-r border-border bg-card overflow-hidden flex flex-col">
          {/* Search and Filter */}
          <div className="p-3 space-y-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            {categories.length > 2 && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === "all" ? "All Categories" : cat}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Agent List */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {filteredAgents.map((agent) => (
              <button
                key={agent.agent_id}
                onClick={() => handleAgentSelect(agent)}
                className={cn(
                  "w-full text-left rounded-lg border p-3 transition-all",
                  selectedAgent.agent_id === agent.agent_id
                    ? "border-primary bg-primary/5"
                    : "border-border bg-background hover:border-primary/50 hover:bg-muted/50"
                )}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{agent.icon || "🤖"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground truncate">
                        {agent.display_name || agent.name}
                      </span>
                      {agent.version && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {agent.version}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {agent.short_description}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </div>
              </button>
            ))}

            {filteredAgents.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No agents found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right Panel - Agent Details & Input */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Agent Header */}
          <div className="flex items-start gap-4">
            <span className="text-4xl">{selectedAgent.icon || "🤖"}</span>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-foreground">
                  {selectedAgent.display_name || selectedAgent.name}
                </h2>
                {selectedAgent.version && (
                  <span className="text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    {selectedAgent.version}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedAgent.short_description || selectedAgent.description}
              </p>
            </div>

          </div>

          {/* Description */}
          {selectedAgent.long_description && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Description</h3>
              <p className="text-sm text-muted-foreground">
                {selectedAgent.long_description}
              </p>
            </div>
          )}

          {/* Capabilities */}
          {selectedAgent.capabilities && selectedAgent.capabilities.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Capabilities</h3>
              <ul className="space-y-1">
                {selectedAgent.capabilities.map((cap, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{cap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resources Section */}
          {showMcpResources && (tenantMcpServers.length > 0 || bindingMode === 'dynamic') && (
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  Resources
                </h3>
                {bindingMode === 'dynamic' && (
                  <button
                    onClick={() => setShowAddResourceModal(true)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors"
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add
                  </button>
                )}
              </div>

              {mcpLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading resources...
                </div>
              ) : (
                <>
                  {/* Fixed mode - show dropdown for each tenant resource */}
                  {bindingMode === 'fixed' && tenantMcpServers.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {tenantMcpServers.map((server) => {
                        const resources = getResourcesForType(server.type);
                        return (
                          <div key={server.name} className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              {server.name} <span className="text-red-400">*</span>
                            </label>
                            <select
                              value={resourceBindings[server.name] || ""}
                              onChange={(e) => handleResourceSelect(server.name, e.target.value)}
                              className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="">Select {server.type}...</option>
                              {resources.map((r) => {
                                const resId = r.res_id || r.id || r.uri || r.name;
                                return (
                                  <option key={resId} value={resId}>
                                    {r.name} {r.is_default && "★"}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Dynamic mode - show added resources as chips */}
                  {bindingMode === 'dynamic' && (
                    <div className="flex flex-wrap gap-2">
                      {dynamicResources.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No resources added yet. Click "Add" to attach resources.</p>
                      ) : (
                        dynamicResources.map((r, idx) => (
                          <div
                            key={`${r.name}-${idx}`}
                            className="flex items-center gap-1.5 px-2 py-1 bg-muted rounded text-xs"
                          >
                            <span className="text-foreground font-medium">{r.name}</span>
                            <span className="text-muted-foreground">({r.type})</span>
                            <button
                              onClick={() => handleRemoveDynamicResource(idx)}
                              className="ml-1 text-muted-foreground hover:text-red-400"
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Platform resources - show as info only */}
                  {platformMcpServers.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex flex-wrap gap-2">
                        {platformMcpServers.map((server) => (
                          <div
                            key={server.name}
                            className="flex items-center gap-1 px-2 py-1 bg-muted/50 rounded text-xs text-muted-foreground"
                          >
                            <Check className="h-3 w-3 text-green-500" />
                            <span>{server.name}</span>
                            <span className="opacity-50">(platform)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Example Queries */}
          {selectedAgent.usage_examples && selectedAgent.usage_examples.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-2">Try It</h3>
              <div className="space-y-1">
                {selectedAgent.usage_examples.map((example, i) => (
                  <button
                    key={i}
                    onClick={() => setGoalInput(example)}
                    className="w-full text-left text-sm text-muted-foreground bg-muted/50 hover:bg-muted px-3 py-2 rounded-md transition-colors"
                  >
                    "{example}"
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Input Area - Fixed at bottom */}
        <div className="border-t border-border bg-card p-4">
          <div className="relative">
            {/* @ Search Dropdown */}
            {enableAtCommands && showAtSearch && (
              <AgentSearchDropdown
                query={atSearchQuery}
                catalog={searchCatalog}
                onSelect={handleAtSearchSelect}
                onClose={handleAtSearchClose}
                onSubmitWithoutSelect={handleAtSearchClose}
              />
            )}
            <textarea
              ref={inputRef}
              value={goalInput}
              onChange={handleGoalInputChange}
              onKeyDown={handleKeyDown}
              placeholder={selectedAgent.usage_examples?.[0] || (enableAtCommands ? "What would you like to do? Type @ for skills..." : "What would you like to do?")}
              className="w-full min-h-[100px] px-4 py-3 pr-14 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              rows={3}
            />
            <button
              onClick={handleSubmit}
              disabled={!canSubmit() || isSubmitting}
              className={cn(
                "absolute right-3 bottom-3 p-2 rounded-lg transition-colors",
                canSubmit() && !isSubmitting
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              )}
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <SendHorizonal className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Press Cmd+Enter to send
          </p>
        </div>
      </div>

      {/* Add Resource Modal (Dynamic mode) */}
      {showAddResourceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-sm font-medium text-foreground">Add Resource</h3>
              <button
                onClick={() => setShowAddResourceModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Semantic name */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Name (semantic)
                </label>
                <input
                  type="text"
                  value={newResourceName}
                  onChange={(e) => setNewResourceName(e.target.value)}
                  placeholder="e.g., source_db, target_repo"
                  className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Type dropdown */}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Type
                </label>
                <select
                  value={newResourceType}
                  onChange={(e) => {
                    setNewResourceType(e.target.value);
                    setNewResourceId("");
                  }}
                  className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Select type...</option>
                  {availableMcpServers.map((server) => (
                    <option key={server.name} value={server.type || server.name}>
                      {server.display_name || server.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Resource dropdown */}
              {newResourceType && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Resource
                  </label>
                  <select
                    value={newResourceId}
                    onChange={(e) => setNewResourceId(e.target.value)}
                    className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select resource...</option>
                    {getResourcesForType(newResourceType).map((r) => {
                      const resId = r.res_id || r.id || r.uri || r.name;
                      return (
                        <option key={resId} value={resId}>
                          {r.name} {r.is_default && "★"}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
              <button
                onClick={() => setShowAddResourceModal(false)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDynamicResource}
                disabled={!newResourceName.trim() || !newResourceType || !newResourceId}
                className={cn(
                  "px-3 py-1.5 text-sm rounded transition-colors",
                  newResourceName.trim() && newResourceType && newResourceId
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
